import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

/**
 * Dado un bloque snapshot sin serviceIds, busca el bloque del live config
 * cuyo rango horario esté más próximo (por extremos start/end en minutos UTC).
 * Se filtra por días en común si el snapshot tiene días definidos.
 * Úsalo como último fallback cuando la clave exacta y el branchId no matchean
 * (p.ej. el live fue acortado de 16-21 a 16-19 después de que se tomó el snapshot).
 */
export const findClosestLiveBlock = (snapshotHour, liveHours) => {
  if (!liveHours?.length) return null;

  const snapshotDays = new Set(snapshotHour.days || []);
  const candidates = liveHours.filter(
    (h) =>
      h?.serviceIds?.length &&
      (snapshotDays.size === 0 || (h.days || []).some((d) => snapshotDays.has(d))),
  );
  if (!candidates.length) return null;

  const snapStartMin =
    dayjs(snapshotHour.start).utc().hour() * 60 +
    dayjs(snapshotHour.start).utc().minute();
  const snapEndMin =
    dayjs(snapshotHour.end).utc().hour() * 60 +
    dayjs(snapshotHour.end).utc().minute();

  let closestBlock = null;
  let minDist = Infinity;

  for (const h of candidates) {
    const liveStartMin =
      dayjs(h.start).utc().hour() * 60 + dayjs(h.start).utc().minute();
    const liveEndMin =
      dayjs(h.end).utc().hour() * 60 + dayjs(h.end).utc().minute();
    const dist = Math.min(
      Math.abs(snapStartMin - liveStartMin),
      Math.abs(snapStartMin - liveEndMin),
      Math.abs(snapEndMin - liveStartMin),
      Math.abs(snapEndMin - liveEndMin),
    );
    if (dist < minDist) {
      minDist = dist;
      closestBlock = h;
    }
  }

  return closestBlock;
};

export const hourBranchId = (hour) =>
  hour?.branchId?.toString?.() || hour?.branchId || null;

const buildBlockKey = (h) => {
  const startTime = dayjs(h.start).utc().format('HH:mm');
  const endTime = dayjs(h.end).utc().format('HH:mm');
  return `${startTime}|${endTime}`;
};

const unionServiceIds = (hours) => [
  ...new Set((hours || []).flatMap((h) => h.serviceIds || []).map(String)),
];

/** Hereda serviceIds del live config cuando el snapshot del extraDay no los trae. */
export const mergeExtraDayHours = (liveHours, snapshotHours) => {
  if (!snapshotHours?.length) return [];
  if (!liveHours?.length) return snapshotHours;

  const liveByKey = new Map(liveHours.map((h) => [buildBlockKey(h), h]));

  return snapshotHours.map((snapshotHour) => {
    if (snapshotHour.serviceIds?.length) return snapshotHour;

    const liveHour = liveByKey.get(buildBlockKey(snapshotHour));
    if (liveHour?.serviceIds?.length) {
      return { ...snapshotHour, serviceIds: liveHour.serviceIds };
    }

    const branchId = hourBranchId(snapshotHour);
    if (branchId) {
      const atBranch = unionServiceIds(
        liveHours.filter((h) => hourBranchId(h) === branchId),
      );
      if (atBranch.length) {
        return { ...snapshotHour, serviceIds: atBranch };
      }
    }

    // Aproximación: el live cambió el rango horario después del snapshot →
    // heredar serviceIds del bloque live con el extremo más cercano.
    const closest = findClosestLiveBlock(snapshotHour, liveHours);
    if (closest) {
      return { ...snapshotHour, serviceIds: closest.serviceIds };
    }

    return snapshotHour;
  });
};

/** Horas live filtradas por sede y servicio (portal). */
export const getFilteredScheduleHours = ({
  hours,
  branchId,
  serviceId,
  isPerBlock,
}) => {
  let filtered = hours || [];
  if (branchId && branchId !== 'all') {
    const bid = String(branchId);
    filtered = filtered.filter((h) => hourBranchId(h) === bid);
  }
  if (isPerBlock && serviceId) {
    const sid = String(serviceId);
    filtered = filtered.filter((h) =>
      (h.serviceIds || []).some((id) => String(id) === sid),
    );
  }
  return filtered;
};

/** Mapa lu/ma/mi… para pintar el calendario según filtros activos (no depende del día seleccionado). */
export const buildCalendarWeekSchedule = (hours) => {
  const week = { lu: {}, ma: {}, mi: {}, ju: {}, vi: {}, sá: {}, do: {} };
  (hours || []).forEach((h) => {
    const start = dayjs(h.start).utc();
    const end = dayjs(h.end).utc();
    const blockSize = h?.sizeSlot || null;
    (h.days || []).forEach((dayKey) => {
      if (!week[dayKey]) return;
      const entriesLength = Object.keys(week[dayKey]).length;
      week[dayKey] =
        entriesLength === 0
          ? { first: [start, end, blockSize] }
          : entriesLength === 1
            ? { ...week[dayKey], second: [start, end, blockSize] }
            : { ...week[dayKey] };
    });
  });
  return week;
};

const collectServiceIdsFromHours = (hours, branchId) => {
  const ids = new Set();
  const bid = branchId ? String(branchId) : null;

  (hours || []).forEach((h) => {
    if (bid && hourBranchId(h) !== bid) return;
    (h.serviceIds || []).forEach((id) => ids.add(String(id)));
  });

  return ids;
};

/** IDs de servicio perBlock: agenda semanal + snapshots de extraDay. */
export const getDoctorPerBlockServiceIds = (liveScheduleConfig, allTurns) => {
  const ids = new Set();
  const liveHours = liveScheduleConfig?.hours || [];

  (liveScheduleConfig?.service || []).forEach((id) => {
    if (id != null) ids.add(String(id));
  });
  liveHours.forEach((h) => {
    (h.serviceIds || []).forEach((id) => {
      if (id != null) ids.add(String(id));
    });
  });
  (allTurns || []).forEach((turn) => {
    if (!turn?.extraDay || !turn?.scheduleConfig?.hours?.length) return;
    turn.scheduleConfig.hours.forEach((h) => {
      if (h.serviceIds?.length) {
        h.serviceIds.forEach((id) => {
          if (id != null) ids.add(String(id));
        });
      } else {
        // El bloque snapshot no tiene serviceIds (el live cambió el rango horario):
        // aproximar por el bloque live con el extremo más cercano.
        const closest = findClosestLiveBlock(h, liveHours);
        (closest?.serviceIds || []).forEach((id) => {
          if (id != null) ids.add(String(id));
        });
      }
    });
  });
  return Array.from(ids);
};

/**
 * Servicios ofrecidos en una sede: horarios semanales + snapshots de extraDay en esa sede.
 */
export const getServiceIdsAvailableAtBranch = (
  liveHours,
  allTurns,
  branchId,
) => {
  if (!branchId || branchId === 'all') {
    return null;
  }

  const bid = String(branchId);
  const ids = collectServiceIdsFromHours(liveHours, bid);

  (allTurns || []).forEach((turn) => {
    if (!turn?.extraDay || !turn?.scheduleConfig?.hours?.length) return;
    const merged = mergeExtraDayHours(liveHours, turn.scheduleConfig.hours);
    collectServiceIdsFromHours(merged, bid).forEach((id) => ids.add(id));
  });

  return ids;
};

export const isServiceAvailableAtBranch = (
  serviceId,
  branchId,
  liveHours,
  allTurns,
) => {
  if (!serviceId || !branchId || branchId === 'all') return true;

  const sid = String(serviceId);
  const bid = String(branchId);
  const available = getServiceIdsAvailableAtBranch(liveHours, allTurns, bid);
  return available?.has(sid) ?? false;
};

/**
 * Para disabledDate: el extraDay debe tener al menos un bloque que cumpla sede y/o servicio activos.
 */
export const extraDayMatchesFilters = ({
  turn,
  liveHours,
  selectedBranchId,
  selectedServiceId,
}) => {
  if (!turn?.extraDay || !turn?.scheduleConfig?.hours?.length) {
    return true;
  }

  const merged = mergeExtraDayHours(liveHours, turn.scheduleConfig.hours);
  if (!merged.length) return false;

  const needsBranch =
    selectedBranchId && selectedBranchId !== 'all';
  const needsService = Boolean(selectedServiceId);

  if (!needsBranch && !needsService) return true;

  const matchesMerged = merged.some((h) => {
    const branchOk =
      !needsBranch || hourBranchId(h) === String(selectedBranchId);
    const serviceOk =
      !needsService ||
      (h.serviceIds || []).some(
        (id) => String(id) === String(selectedServiceId),
      );
    return branchOk && serviceOk;
  });

  return matchesMerged;
};
